import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './App.css';
import AudioPlayer, { Track, AudioPlayerHandle } from './components/AudioPlayer';
import ShowList from './components/ShowList';
import BackgroundManager from './components/BackgroundManager';
import LiveStreamTicker from './components/LiveStreamTicker';
import LiveStreamButton from './components/LiveStreamButton';
import { useLiveStatus } from './hooks/useLiveStatus';
import { useMediaSession } from './hooks/useMediaSession';
import { useAudio } from './audio/AudioProvider';
import SeriesBrowse from './components/SeriesBrowse';
import { apiService, API_BASE_URL, Show, PageContent } from './services/api';
import logo from './logo.png';
import popoutStar from './assets/popout-star.png';
const lockScreenArt = window.location.origin + '/web-app-manifest-512x512.png';

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function formatDuration(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function App() {
  // Set window name so mini player can target this tab
  useEffect(() => { window.name = 'gfr-main'; }, []);
  const location = useLocation();
  const playerRef = useRef<AudioPlayerHandle | null>(null);
  const [shows, setShows] = useState<Show[]>([]);
  const [currentShowIndex, setCurrentShowIndex] = useState(0);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const [showSelectionVersion, setShowSelectionVersion] = useState(0);
  const [activePage, setActivePage] = useState<'shows' | 'about' | 'events' | 'contact' | null>(null);
  const [pageCache, setPageCache] = useState<Record<string, PageContent>>({});
  const [contactCopied, setContactCopied] = useState(false);
  const [contactHovered, setContactHovered] = useState(false);
  const [autoPlayMode, setAutoPlayMode] = useState<'off' | 'sequential' | 'match'>('off');
  const [masterShowIndex, setMasterShowIndex] = useState(0);
  const [playedShowIds, setPlayedShowIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchInteractive, setSearchInteractive] = useState(true);

  // Live stream status and audio context
  const { isLive, nowPlaying, showTitle, streamUrl } = useLiveStatus();
  const { source, playLive, stopLive, trackNowPlaying, progress: audioProgress, remotePlaybackAvailable, remotePlaybackState, promptRemotePlayback } = useAudio();
  const livePlaying = source === "live";

  // Media Session API — lock screen controls, AirPlay Now Playing, hardware keys
  const handleMediaPlay = useCallback(() => {
    bcRef.current?.postMessage({ type: 'playing' });
    if (source === "live") playLive(streamUrl);
    else if (source === "track") playerRef.current?.resumeOrStart();
  }, [source, playLive, streamUrl]);

  const handleMediaPause = useCallback(() => {
    if (source === "live") stopLive();
    else if (source === "track") playerRef.current?.pause();
  }, [source, stopLive]);

  const handleMediaNext = useCallback(() => {
    playerRef.current?.next();
  }, []);

  const handleMediaPrev = useCallback(() => {
    playerRef.current?.prev();
  }, []);

  useMediaSession({
    source,
    trackTitle: trackNowPlaying,
    showName: shows[currentShowIndex]?.title || "Glue Factory Radio",
    liveNowPlaying: nowPlaying,
    liveShowTitle: showTitle,
    artworkUrl: lockScreenArt,
    onPlay: handleMediaPlay,
    onPause: handleMediaPause,
    onNext: handleMediaNext,
    onPrev: handleMediaPrev,
  });
  
  // Coordinate audio with mini player via BroadcastChannel
  const bcRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const bc = new BroadcastChannel('gfr-miniplayer');
    bcRef.current = bc;
    bc.onmessage = (e) => {
      if (e.data?.type === 'opened' || e.data?.type === 'playing') {
        stopLive();
        playerRef.current?.pause();
      }
    };
    return () => { bc.close(); bcRef.current = null; };
  }, [stopLive]);

  // Custom live stream label from admin (defaults to "LIVE NOW")
  const liveLabel = pageCache.live_label?.content || 'LIVE NOW';

  // Determine what to display in the ticker based on which audio source is playing
  const tickerDisplayText = useMemo(() => {
    if (source === "live") {
      if (nowPlaying && !liveLabel.includes(nowPlaying)) {
        return `${liveLabel}: ${nowPlaying}`;
      }
      return liveLabel;
    }
    if (source === "track") {
      return `PLAYING NOW: ${trackNowPlaying ?? "Track"}`;
    }
    return "GLUE FACTORY RADIO";
  }, [source, nowPlaying, trackNowPlaying, liveLabel]);
  
  const tickerIsEmpty = source === "none";
  
  // Fetch shows on component mount, resolve deep link if present
  useEffect(() => {
    const fetchShows = async () => {
      try {
        setIsLoading(true);
        console.log('App: Fetching shows...');
        const fetchedShows = await apiService.getShows();
        console.log('App: Fetched', fetchedShows.length, 'shows');
        fetchedShows.forEach((show, idx) => {
          console.log(`App: Show ${idx}: "${show.title}" with ${show.tracks?.length || 0} tracks`);
        });
        setShows(fetchedShows);

        // Resolve deep link: /show/...
        // Formats: /show/{series}/{title}/Ep-{num}, /show/{series}/Ep-{num}, /show/{slug}
        if (location.pathname.startsWith('/show/') && fetchedShows.length > 0) {
          const path = location.pathname.slice('/show/'.length);
          let idx = -1;

          // Try series episode with title: {series}/{title}/Ep-{num}
          const withTitle = path.match(/^(.+?)\/(.+?)\/Ep-(\d+)$/);
          if (withTitle) {
            const [, seriesSlug, , epNum] = withTitle;
            idx = fetchedShows.findIndex(s =>
              s.series_title && s.episode_number &&
              generateSlug(s.series_title) === seriesSlug &&
              s.episode_number === parseInt(epNum, 10)
            );
          }

          // Try series episode without title: {series}/Ep-{num}
          if (idx === -1) {
            const noTitle = path.match(/^(.+?)\/Ep-(\d+)$/);
            if (noTitle) {
              const [, seriesSlug, epNum] = noTitle;
              idx = fetchedShows.findIndex(s =>
                s.series_title && s.episode_number &&
                generateSlug(s.series_title) === seriesSlug &&
                s.episode_number === parseInt(epNum, 10)
              );
            }
          }

          // Fall back to standalone: {slug}
          if (idx === -1) {
            idx = fetchedShows.findIndex(s => generateSlug(s.title) === path);
          }

          if (idx !== -1) {
            setCurrentShowIndex(idx);
            setArchiveExpanded(true);
            setShowSelectionVersion(v => v + 1);
          }
        }

        setError(null);
      } catch (err) {
        console.error('App: Error fetching shows:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch shows');
      } finally {
        setIsLoading(false);
      }
    };

    fetchShows();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Monitor state changes for debugging
  useEffect(() => {
    console.log('App: State changed - Show:', currentShowIndex, 'Track:', currentTrackIndex);
  }, [currentShowIndex, currentTrackIndex]);

  // Debug audio source changes
  useEffect(() => {
    console.log("[AUDIO SOURCE]", source);
  }, [source]);

  // Fetch all page content on mount
  useEffect(() => {
    const fetchAllPages = async () => {
      try {
        const [about, events, contact, liveLabel] = await Promise.all([
          apiService.getPageContent('about'),
          apiService.getPageContent('events'),
          apiService.getPageContent('contact'),
          apiService.getPageContent('live_label'),
        ]);
        setPageCache({
          about: about,
          events: events,
          contact: contact,
          live_label: liveLabel,
        });
      } catch (err) {
        console.error('Error fetching page content:', err);
      }
    };
    fetchAllPages();
  }, []);
  
  // Update URL to reflect the currently loaded show
  const updateShowUrl = useCallback((show: Show) => {
    let path: string;
    if (show.series_title && show.episode_number) {
      const seriesSlug = generateSlug(show.series_title);
      if (show.title) {
        const titleSlug = generateSlug(show.title);
        path = `/show/${seriesSlug}/${titleSlug}/Ep-${show.episode_number}`;
      } else {
        path = `/show/${seriesSlug}/Ep-${show.episode_number}`;
      }
    } else {
      path = `/show/${generateSlug(show.title)}`;
    }
    window.history.replaceState(null, '', path);
  }, []);

  // Handle show selection with pin/scroll (search results, series page, deep links)
  const handleShowChange = useCallback((newShowIndex: number) => {
    console.log('App: Changing show to:', newShowIndex);

    setCurrentShowIndex(prev => {
      if (prev !== newShowIndex) {
        setCurrentTrackIndex(0);
      }
      return newShowIndex;
    });

    // User explicitly selected — becomes tag-match master
    setMasterShowIndex(newShowIndex);
    setPlayedShowIds(new Set());

    // Signal ShowList to expand + scroll to this show
    setShowSelectionVersion(v => v + 1);

    // Update URL
    if (shows[newShowIndex]) {
      updateShowUrl(shows[newShowIndex]);
    }

    // Always trigger playback (handles first load where show is already selected)
    requestAnimationFrame(() => {
      bcRef.current?.postMessage({ type: 'playing' });
      playerRef.current?.playFromUI(0);
    });
  }, [shows, updateShowUrl]);

  // Handle show selection from archive play button (load + play, no pin/scroll)
  const handleShowLoad = useCallback((newShowIndex: number) => {
    console.log('App: Loading show:', newShowIndex);

    setCurrentShowIndex(prev => {
      if (prev !== newShowIndex) {
        setCurrentTrackIndex(0);
      }
      return newShowIndex;
    });

    if (shows[newShowIndex]) {
      updateShowUrl(shows[newShowIndex]);
    }

    // Use setTimeout instead of requestAnimationFrame so playback starts
    // even when the tab is hidden (rAF is suspended in background tabs).
    setTimeout(() => {
      bcRef.current?.postMessage({ type: 'playing' });
      playerRef.current?.playFromUI(0);
    }, 0);
  }, [shows, updateShowUrl]);
  
  // Handle track selection from ShowList
  const handleTrackSelect = (showIndex: number, trackIndex: number) => {
    console.log('App: handleTrackSelect CALLED!');
    console.log('App: Track selected - Show:', showIndex, 'Track:', trackIndex);
    console.log('App: Previous state - Show:', currentShowIndex, 'Track:', currentTrackIndex);

    // 1) Switch show/track state so UI reflects selection
    if (showIndex !== currentShowIndex) {
      setCurrentShowIndex(showIndex);
      // User manually picked a new show — it becomes the tag-match master
      setMasterShowIndex(showIndex);
      setPlayedShowIds(new Set());
    }
    setCurrentTrackIndex(trackIndex);

    // Never auto-scroll on track clicks — preserve the user's scroll position.
    // Auto-scroll only happens via handleShowChange (play button, search, deep link).

    // 2) Update URL
    if (shows[showIndex]) {
      updateShowUrl(shows[showIndex]);
    }

    // 3) MOST IMPORTANT: start playback in the same user click
    //    This satisfies autoplay policies reliably.
    //    We also pass the target index explicitly.
    requestAnimationFrame(() => {
      bcRef.current?.postMessage({ type: 'playing' });
      playerRef.current?.playFromUI(trackIndex);
    });

    console.log('App: Updated currentShowIndex to:', showIndex, 'and currentTrackIndex to:', trackIndex);
  };
  
  // Search overlay handlers
  const openSearch = useCallback(() => {
    setSearchInteractive(false);
    setSearchOpen(true);
    setSearchQuery('');
    setSelectedTags([]);
    // Auto-focus search input after overlay renders
    setTimeout(() => searchInputRef.current?.focus(), 100);
    // Block pointer interaction briefly to prevent iOS touch passthrough
    setTimeout(() => setSearchInteractive(true), 600);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setSelectedTags([]);
  }, []);

  const addTag = useCallback((tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev : [...prev, tag]);
  }, []);

  const removeTag = useCallback((tag: string) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  }, []);

  const handleSearchSelect = useCallback((showIndex: number) => {
    handleShowChange(showIndex);
    closeSearch();
  }, [handleShowChange, closeSearch]);

  // Filter shows based on search query and selected tags
  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const hasQuery = query.length > 0;
    const hasTags = selectedTags.length > 0;

    // No filters active = no results (empty state)
    if (!hasQuery && !hasTags) return [];

    return shows
      .map((show, index) => {
        // Tag filter (AND logic: show must have all selected tags)
        if (hasTags) {
          const showTags = show.tags || [];
          const hasAllTags = selectedTags.every(tag => showTags.includes(tag));
          if (!hasAllTags) return null;
        }

        // Text filter
        let score = 0;
        if (hasQuery) {
          const titleMatch = show.title.toLowerCase().includes(query);
          const descMatch = show.description?.toLowerCase().includes(query);
          const seriesMatch = show.series_title?.toLowerCase().includes(query);
          if (titleMatch) score = 2;
          else if (seriesMatch) score = 2;
          else if (descMatch) score = 1;
          else return null; // No text match
        } else {
          score = 1; // Tags only, equal relevance
        }

        return { show, index, score };
      })
      .filter((r): r is { show: Show; index: number; score: number } => r !== null)
      .sort((a, b) => {
        // Sort by relevance (title > description), then newest first
        if (b.score !== a.score) return b.score - a.score;
        return new Date(b.show.created_date).getTime() - new Date(a.show.created_date).getTime();
      });
  }, [shows, searchQuery, selectedTags]);

  const availableTags = useMemo(() => {
    // Find shows that match ALL selected tags (or all shows if none selected)
    const matchingShows = selectedTags.length === 0
      ? shows
      : shows.filter(show => {
          const showTags = show.tags || [];
          return selectedTags.every(tag => showTags.includes(tag));
        });
    // Collect tags from matching shows, excluding already-selected ones
    const compatibleTags = new Set<string>();
    matchingShows.forEach(show => {
      (show.tags || []).forEach(tag => {
        if (!selectedTags.includes(tag)) {
          compatibleTags.add(tag);
        }
      });
    });
    return Array.from(compatibleTags).sort();
  }, [shows, selectedTags]);

  // Handle track navigation from AudioPlayer (currently unused but kept for future use)
  // const handleTrackChange = (newTrackIndex: number) => {
  //   setCurrentTrackIndex(newTrackIndex);
  // };
  
  // Auto-play: pick next show when current show's last track ends
  const handleShowEnded = useCallback(() => {
    if (autoPlayMode === 'off') return;

    const currentShow = shows[currentShowIndex];
    if (!currentShow) return;

    // Track what we've played
    const newPlayed = new Set(playedShowIds);
    newPlayed.add(currentShow.id);

    let nextIndex = -1;

    if (autoPlayMode === 'sequential') {
      // Next show in list order (shows are newest-first)
      for (let i = currentShowIndex + 1; i < shows.length; i++) {
        if (!newPlayed.has(shows[i].id)) {
          nextIndex = i;
          break;
        }
      }
      // Wrap around
      if (nextIndex === -1) {
        for (let i = 0; i < currentShowIndex; i++) {
          if (!newPlayed.has(shows[i].id)) {
            nextIndex = i;
            break;
          }
        }
      }
    } else if (autoPlayMode === 'match') {
      // Tag-match: compare against master show's tags
      const masterShow = shows[masterShowIndex] ?? currentShow;
      const masterTags = new Set(masterShow.tags ?? []);

      if (masterTags.size === 0) {
        // No tags on master — fall back to sequential
        for (let i = currentShowIndex + 1; i < shows.length; i++) {
          if (!newPlayed.has(shows[i].id)) {
            nextIndex = i;
            break;
          }
        }
      } else {
        // Score all unplayed shows by tag similarity to master
        const candidates = shows
          .map((show, idx) => {
            if (idx === currentShowIndex || newPlayed.has(show.id)) return null;
            const showTags = show.tags ?? [];
            const similarity = showTags.filter(t => masterTags.has(t)).length;
            return { idx, similarity, date: new Date(show.created_date).getTime() };
          })
          .filter((c): c is { idx: number; similarity: number; date: number } => c !== null);

        // Sort: highest similarity first, then newest first
        candidates.sort((a, b) => {
          if (b.similarity !== a.similarity) return b.similarity - a.similarity;
          return b.date - a.date;
        });

        if (candidates.length > 0) {
          nextIndex = candidates[0].idx;
        }
      }
    }

    if (nextIndex === -1) {
      // All shows played — reset and stop
      setPlayedShowIds(new Set());
      return;
    }

    setPlayedShowIds(newPlayed);
    handleShowLoad(nextIndex);
  }, [autoPlayMode, shows, currentShowIndex, masterShowIndex, playedShowIds, handleShowLoad]);
  
  // Loading state - render blank page to avoid flash of text
  if (isLoading) {
    return <div />;
  }
  
  // Error state
  if (error) {
    return (
      <div className="error-state">
        <h2>Error Loading Shows</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }
  
  // No shows state
  if (shows.length === 0) {
    return (
      <div className="no-shows-state">
        <h2>No Shows Available</h2>
        <p>No shows have been uploaded yet.</p>
        <p>Check back later or contact an administrator.</p>
      </div>
    );
  }
  
  // Ensure indices are valid
  const validShowIndex = Math.min(Math.max(0, currentShowIndex), shows.length - 1);
  
  // Convert show tracks to EpisodePlayer format
  const convertShowToTracks = (show: Show): Track[] => {
    return show.tracks.map(track => ({
      src: `${API_BASE_URL}/api${track.url.split('/').map(s => encodeURIComponent(s)).join('/')}`,
      title: track.title
    }));
  };

  // Get tracks for current show
  const currentTracks = shows[validShowIndex] ? convertShowToTracks(shows[validShowIndex]) : [];
  
  console.log('App: Final values - validShowIndex:', validShowIndex, 'tracks:', currentTracks.length);
  if (currentTracks.length > 0) {
    console.log('App: First track URL:', currentTracks[0].src);
  }
  
  return (
    <>
      <BackgroundManager />
      <LiveStreamTicker
        displayText={tickerDisplayText}
        isEmpty={tickerIsEmpty}
      />

      {/* Pop-out mini player button */}
      <button
        className="popout-btn"
        onClick={() => {
          const currentShow = shows[validShowIndex];
          const searchParams = new URLSearchParams();
          if (currentShow) {
            searchParams.set('show', String(currentShow.id));
            searchParams.set('track', String(currentTrackIndex));
          }
          if (source === 'live') {
            searchParams.set('autoplay', 'live');
          } else if (source === 'track') {
            searchParams.set('autoplay', 'track');
            searchParams.set('time', String(Math.floor(audioProgress)));
          }
          const params = searchParams.toString() ? `?${searchParams.toString()}` : '';
          window.open(`/mini${params}`, 'gfr-mini', 'width=280,height=203,resizable=yes');
        }}
        title="Pop out mini player"
      >
        <img className="popout-star" src={popoutStar} alt="" width="55" height="55" />
        <svg className="popout-hover-svg" viewBox="0 0 50 50" width="55" height="55">
          <defs>
            <path id="topArc" d="M 11,25 A 14,14 0 0,1 39,25" fill="none" />
            <path id="bottomArc" d="M 12,25 A 13,13 0 0,0 38,25" fill="none" />
          </defs>
          <circle cx="25" cy="25" r="24" fill="none" stroke="none" />
          <g transform="rotate(-45, 25, 25)">
            <text fontFamily="'Roboto Mono', monospace" fontSize="9" fontWeight="700" fill="#ff1900">
              <textPath href="#topArc" startOffset="50%" textAnchor="middle">POP-OUT</textPath>
            </text>
            <text fontFamily="'Roboto Mono', monospace" fontSize="9" fontWeight="700" fill="#ff1900" dy="9">
              <textPath href="#bottomArc" startOffset="50%" textAnchor="middle">PLAYER</textPath>
            </text>
          </g>
        </svg>
      </button>

      {/* Logo at center top */}
      <div className="logo-container">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            window.history.replaceState(null, '', '/');
            setArchiveExpanded(false);
            setActivePage(null);
            closeSearch();
          }}
        >
          <img src={logo} alt="Glue Factory Radio Logo" className="logo-image" />
        </a>
      </div>

      {/* LIVE NOW button - fixed at true center of viewport */}
      <LiveStreamButton
        isLive={isLive}
        isPlaying={livePlaying}
        nowPlaying={nowPlaying ?? undefined}
        liveLabel={liveLabel}
        onClick={() => { if (livePlaying) { stopLive(); } else { bcRef.current?.postMessage({ type: 'playing' }); playLive(streamUrl); } }}
      />

      {/* Tap-away overlay: covers area above archive to close it */}
      {archiveExpanded && (
        <div
          className="archive-dismiss-overlay"
          onClick={() => { setArchiveExpanded(false); setActivePage(null); closeSearch(); }}
        />
      )}

      {/* Archive footer - fixed to bottom, expands upward */}
      <div className={`App-footer-archive ${archiveExpanded ? 'expanded' : ''}`}>
        {/* Sticky block: header + controls + search + info stay pinned */}
        <div className="archive-sticky-block">
          <div
            className="archive-header-row clickable"
            onClick={() => { setArchiveExpanded(!archiveExpanded); if (archiveExpanded) { setActivePage(null); closeSearch(); } }}
          >
            <span className="archive-header-text">{archiveExpanded ? 'CLOSE THE ARCHIVE' : 'OPEN THE ARCHIVE'}</span>
            {remotePlaybackAvailable && (
              <button
                className={`archive-header-stream-btn ${remotePlaybackState !== 'disconnected' ? 'archive-header-stream-btn-active' : ''}`}
                onClick={(e) => { e.stopPropagation(); promptRemotePlayback(); }}
                title={remotePlaybackState === 'connected' ? 'Streaming (tap to change)' : 'Stream to device'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 16.1A5 5 0 0 1 5.9 20M2 12.05A9 9 0 0 1 9.95 20M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6" />
                  <line x1="2" y1="20" x2="2.01" y2="20" />
                </svg>
              </button>
            )}
            <span className={`archive-arrow ${archiveExpanded ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>

          <AudioPlayer
            key={shows[validShowIndex]?.id ?? validShowIndex}
            ref={playerRef}
            tracks={currentTracks}
            initialIndex={currentTrackIndex}
            showName={(() => {
              const show = shows[validShowIndex];
              if (!show) return "CD Mode";
              const parts: string[] = [];
              if (show.series_title) parts.push(show.series_title);
              if (show.title) parts.push(show.title);
              if (show.episode_number && !show.hide_episode_numbers) parts.push(`Ep. ${show.episode_number}`);
              return parts.join(': ') || "CD Mode";
            })()}
            archiveExpanded={archiveExpanded}
            onArchiveToggle={() => setArchiveExpanded(!archiveExpanded)}
            onSearchOpen={openSearch}
            autoPlayMode={autoPlayMode}
            onAutoPlayChange={setAutoPlayMode}
            onPlay={() => { bcRef.current?.postMessage({ type: 'playing' }); if (shows[validShowIndex]) updateShowUrl(shows[validShowIndex]); }}
            onShowNavigate={() => { setShowSelectionVersion(v => v + 1); }}
            onShowEnded={autoPlayMode !== 'off' ? handleShowEnded : undefined}
          />
        </div>

        {archiveExpanded && (
          <>
            <ShowList
              shows={shows}
              currentShowIndex={validShowIndex}
              showSelectionVersion={showSelectionVersion}
              onShowSelect={handleShowLoad}
              onTrackSelect={handleTrackSelect}
            />
            {/* Bottom nav footer - fixed within expanded archive */}
            <div className="archive-nav-footer">
              <span className="archive-nav-item" onClick={() => setActivePage('shows')}>SHOWS</span>
              <span className="archive-nav-item" onClick={() => setActivePage('about')}>ABOUT</span>
              <span className="archive-nav-item" onClick={() => setActivePage('events')}>EVENTS</span>
              <span className="archive-nav-item" onClick={() => setActivePage('contact')}>CONTACT</span>
            </div>
          </>
        )}
      </div>

      {/* Page overlay for nav items */}
      {activePage && (
        <div className={`page-overlay${activePage === 'shows' ? ' page-overlay-list' : ''}`}>
          <button className="page-overlay-close" onClick={() => setActivePage(null)} aria-label="Close">
          </button>
          <div className="page-overlay-content">
            {activePage === 'shows' ? (
              <SeriesBrowse onEpisodeSelect={(episode) => {
                const idx = shows.findIndex(s => s.id === episode.id);
                if (idx !== -1) {
                  handleShowChange(idx);
                  setActivePage(null);
                }
              }} />
            ) : activePage && pageCache[activePage]?.content ? (
              activePage === 'contact' ? (
                <div
                  className={`page-text contact-copyable ${contactCopied ? 'copied' : ''}`}
                  onClick={() => {
                    navigator.clipboard.writeText(pageCache[activePage].content);
                    setContactCopied(true);
                    setTimeout(() => setContactCopied(false), 2000);
                  }}
                  onMouseEnter={() => setContactHovered(true)}
                  onMouseLeave={() => setContactHovered(false)}
                >
                  {/* Email stays in DOM to maintain hover area size */}
                  <span className={`contact-email ${contactCopied || (contactHovered && window.matchMedia('(hover: hover)').matches) ? 'hidden' : ''}`}>
                    {pageCache[activePage].content}
                  </span>
                  {/* Overlay text positioned on top */}
                  {contactCopied ? (
                    <span className="contact-overlay">COPIED!</span>
                  ) : contactHovered && window.matchMedia('(hover: hover)').matches ? (
                    <span className="contact-overlay">CLICK TO COPY</span>
                  ) : null}
                </div>
              ) : (
                <div className="page-text">{pageCache[activePage].content}</div>
              )
            ) : null}

            {/* Mailchimp signup form - Events page only */}
            {activePage === 'events' && (
              <div className="events-signup">
                <form
                  action="https://gluefactoryradio.us3.list-manage.com/subscribe/post?u=59fdb08d951a9fe15136d1bcf&amp;id=d929eeaa44&amp;f_id=00625ee1f0"
                  method="post"
                  target="_self"
                  className="events-signup-form"
                >
                  <label htmlFor="mce-EMAIL" className="events-signup-label">
                    Subscribe for event updates
                  </label>
                  <div className="events-signup-input-row">
                    <input
                      type="email"
                      name="EMAIL"
                      id="mce-EMAIL"
                      placeholder="Enter your email"
                      required
                      className="events-signup-input"
                    />
                    <button type="submit" className="events-signup-button">
                      Subscribe
                    </button>
                  </div>
                  {/* Honeypot field for bot protection */}
                  <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                    <input type="text" name="b_59fdb08d951a9fe15136d1bcf_d929eeaa44" tabIndex={-1} defaultValue="" />
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search overlay */}
      {searchOpen && (
        <div className="page-overlay search-overlay">
          <button className="page-overlay-close" onClick={closeSearch} aria-label="Close search" />
          <div className="search-overlay-content">
            {/* Search input with selected tags */}
            <div className="search-input-wrapper">
              <svg className="search-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              {selectedTags.map(tag => (
                <span key={tag} className="search-tag-selected" onClick={() => removeTag(tag)}>
                  {tag}
                  <span className="search-tag-x">&times;</span>
                </span>
              ))}
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={selectedTags.length > 0 ? '' : 'Search shows...'}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Available tag pills */}
            {availableTags.length > 0 && (
              <div className="search-available-tags" style={{ pointerEvents: searchInteractive ? 'auto' : 'none' }}>
                {availableTags.map(tag => (
                  <span key={tag} className="search-tag-pill" onClick={() => addTag(tag)}>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Results or empty state */}
            <div className="search-results">
              {searchQuery.trim() === '' && selectedTags.length === 0 ? (
                null
              ) : searchResults.length === 0 ? (
                <div className="search-empty-state">No shows found</div>
              ) : (
                searchResults.map(result => (
                  <div
                    key={result.show.id}
                    className="series-episode-item"
                    onClick={() => handleSearchSelect(result.index)}
                  >
                    <span className="episode-info">
                      <span className="episode-title">
                        {result.show.series_title && result.show.episode_number && !result.show.hide_episode_numbers
                          ? `${result.show.series_title}:${result.show.title ? ` ${result.show.title}` : ''} Ep. ${result.show.episode_number}`
                          : result.show.series_title && result.show.episode_number && result.show.hide_episode_numbers
                          ? `${result.show.series_title}${result.show.title ? `: ${result.show.title}` : ''}`
                          : result.show.title}
                      </span>
                    </span>
                    <span className="episode-meta">
                      <span className="episode-tracks">{result.show.total_tracks} track{result.show.total_tracks !== 1 ? 's' : ''}</span>
                      {result.show.tags && result.show.tags.length > 0 && (
                        <span className="episode-tags">
                          {result.show.tags.map(tag => (
                            <span key={tag} className="episode-tag">{tag}</span>
                          ))}
                        </span>
                      )}
                      <span className="episode-duration">{formatDuration(result.show.total_duration)}</span>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
